import * as errors from '../constant/errors';
import { SurveyFlow } from '../constant';
import { BotReply, Context, IBotDetail, IDocument, ISourceWebsite, Intent, SlotsRequired } from 'interface';
import { ControlType, DynamicFieldData } from 'screens/BotFormControls/dynamic-control-types';
import moment from 'moment';
import { FieldValues, FormState } from 'react-hook-form';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';

export const isSurveyLinked = (botDetail: IBotDetail) => {
    return botDetail.surveys !== null && botDetail.surveys?.length > 0;
};

export const isSurveyFlowStart = (botDetail: IBotDetail) => {
    return botDetail.surveys?.[0].surveyFlow === SurveyFlow.START;
};

export const getLastSegment = (value, separator) => {
    var input = value;
    var period = input.lastIndexOf('.');
    var lastValue = input.substring(period + 1);
    return lastValue;
};

export const botErrorMapping = () => ({
    'session is expired': errors.SessionExpiredError,
    'client does not exist': errors.ClientNotExistError,
    [errors.ResponseDelayError?.toLowerCase()]: errors.ResponseDelayError
});

export const getBotErrorMessage = (botResponse: BotReply) => {
    if (!botResponse) return errors.NotProcessedError;

    const responseMessage = botResponse.message?.toLowerCase();
    return botErrorMapping[responseMessage] || errors.NotProcessedError;
};

export const decodeUnicode = (str: string): string => {
    return str.replace(/\\u[\dA-F]{4}/gi, function (match) {
        return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
    });
};

export const formatStringWithLineBreaks = (str: string): string => {
    return decodeUnicode(str.replace(/•/g, '\n•').replace(/:/g, ':\n').replace(/\\n/g, '\n'));
};

export const blurActiveElement = () => {
    const focusedElement = document.activeElement as HTMLElement;
    if (focusedElement) {
        focusedElement.blur();
    }
};
const formInputControllers: Record<string, ControlType> = {
    email: 'email',
    number: 'number',
    dropdown: 'select',
    checkbox: 'checkbox',
    radiobutton: 'radiobutton',
    phone: 'phone',
    default: 'text'
};

export const validatePhoneNumber = (value: string) => {
    if (!value?.trim()) return errors.fieldRequiredError;

    const phoneNumber = parsePhoneNumber(value);
    if (!phoneNumber) return errors.fieldRequiredError;

    const { nationalNumber } = phoneNumber; // Extract national number from phone number (without country code)
    const numericPhoneNumber = value.replace(/\D/g, '');

    if (nationalNumber?.length !== 10) return errors.InvalidPhoneNumberError;
    if (whitelist.map((x) => x.replace(/\D/g, '')).includes(numericPhoneNumber)) return null;
    if (isValidPhoneNumber(value)) return null;
    return errors.InvalidPhoneNumberError;
};

const whitelist = [];

export const createDynamicFormJson = (slotsRequired: SlotsRequired[]): DynamicFieldData[] => {
    console.table(slotsRequired);

    return slotsRequired.map((slotRequired: SlotsRequired): DynamicFieldData => {
        let inputType: ControlType = formInputControllers.default;

        let {
            isMultiSelector,
            multiSelectorType,
            possibleValues,
            displayValues,
            isRequired,
            fieldType,
            heading,
            name,
            value
        } = slotRequired;

        const inputControlType = isMultiSelector ? multiSelectorType : fieldType;
        inputType = formInputControllers[inputControlType] || formInputControllers.default;

        if (isMultiSelector && !['dropdown', 'checkbox'].includes(multiSelectorType) && fieldType === null) {
            fieldType = 'text';
        }

        const isDropdown = inputType === 'select';
        const isPhoneInput = inputType === 'phone';
        const multiSelectOptions = (value: string, index: number) => ({ label: displayValues[index], value });

        const ruleConfigurations = {
            required: isRequired ? { value: true, message: errors.fieldRequiredError } : null
        };

        const dynamicJSON: DynamicFieldData = {
            heading,
            inputType,
            label: name,
            fieldName: name,
            defaultValue: isDropdown ? '' : value,
            options: isMultiSelector ? possibleValues?.map(multiSelectOptions) : [],
            config: ruleConfigurations
        };

        console.log('dynamicJSON: ', dynamicJSON);
        return dynamicJSON;
    });
};

export const logFormErrors = (formState: FormState<FieldValues>) => {
    console.table(
        Object.keys(formState.errors).map((fieldName) => ({
            'Error Field': fieldName,
            'Error Message': formState.errors[fieldName]?.message
        }))
    );
};

export const getAddSessionRequest = (botDetail: IBotDetail) => {
    return {
        user_id: '',
        bot_id: botDetail.botInfo.botId,
        session_id: '',
        survey: {
            campaign_id: botDetail.surveys[0].campaignId
        }
    };
};

export const getBotReplyRequest = ({
    botDetails,
    intentTriggered,
    filledSlots,
    botControllers,
    sessionId,
    userMessage,
    clientMessageList,
    surveyStatus,
    skip
}) => {
    return {
        sessionId: sessionId,
        botId: botDetails.botInfo.botId,
        message: {
            text: userMessage,
            textList: clientMessageList
        },
        triggerIntent: intentTriggered,
        agentFacing: true,
        enabledFeatures: {
            small_talk: botControllers.small_talk,
            website: botControllers.website,
            document: botControllers.document,
            api: botControllers.api,
            answerGeneration: botDetails.botSettings.llm,
            context: botDetails.botSettings.personality
        },
        sessionSlots: null,
        llmParameters: {
            temperature: botDetails.botSettings.temperature,
            topN: botDetails.botSettings.topAnswers,
            maxTokens: null
        },
        filledSlots: filledSlots
    };
};

export const shouldStartSurvey = (botDetail: IBotDetail) => {
    return (
        botDetail.surveys !== null &&
        botDetail.surveys.length > 0 &&
        botDetail.surveys?.[0].surveyFlow === SurveyFlow.START
    );
};

export const isNoSurveyOrResponseTypeAnswer = (responseBy, responseType) => {
    return responseBy !== 'survey' || responseType === 'answer';
};

export const isNoSurveyAndHasIntent = ({ responseBy, intent }) => {
    return intent && responseBy !== 'survey';
};

export const convertFormDataToBotQuery = (data) => {
    console.log('DATA >> ', data);

    let botQuery = '';

    for (var [key, value] of Object.entries(data)) {
        console.log(`${key}: ${value}`);

        if (value instanceof Date) {
            // isNaN wont accept a date in typescript, use date.getTime() instead to produce a number
            value = moment(value).format('DD/MM/yyyy');
        }

        botQuery += `${key}:\n${value}\n\n`;
    }

    return botQuery;
};

export const updateFilledSlots = (
    filledSlots: Record<string, string>,
    slotsRequired: SlotsRequired[]
): Record<string, string> => {
    const slotsLookup = new Map(slotsRequired.map((slot: SlotsRequired) => [slot.name, slot]));

    const updatedSlots = Object.entries(filledSlots).map(([key, value]) => {
        const slot = slotsLookup.get(key);

        if (slot?.isMultiSelector && slot?.displayValues?.length > 0) {
            const index = slot.possibleValues.indexOf(value);
            return [key, index !== -1 ? slot.displayValues[index] : value];
        } else {
            return [key, value];
        }
    });

    return Object.fromEntries(updatedSlots);
};

// Helper function to find corresponding values for dropdown
const findDropdownCorrespondingValue = (slot: SlotsRequired, value: string): string[] => {
    const index = slot.displayValues.indexOf(value);
    return index !== -1 ? [slot.possibleValues[index]] : [value];
};

// Helper function to find corresponding values for checkbox
const findCheckboxCorrespondingValues = (slot: SlotsRequired, values: string[]): string[] => {
    return values.map((value) =>
        slot.displayValues.indexOf(value) !== -1 ? slot.possibleValues[slot.displayValues.indexOf(value)] : ''
    );
};

export const preProcessFilledSlots = (
    filledSlots: Record<string, string>,
    slotsRequired: SlotsRequired[]
): Record<string, string> => {
    const slotsLookup = new Map(slotsRequired.map((slot: SlotsRequired) => [slot.name, slot]));

    return Object.fromEntries(
        Object.entries(filledSlots).map(([key, value]) => {
            const slot = slotsLookup.get(key);

            if (!slot?.isMultiSelector) return [key, value];

            let correspondingValues: string[];

            if (slot.multiSelectorType === 'dropdown') {
                correspondingValues = findDropdownCorrespondingValue(slot, value);
            } else if (slot.multiSelectorType === 'checkbox') {
                correspondingValues = findCheckboxCorrespondingValues(
                    slot,
                    value.split(',').map((val) => val.trim())
                );
            } else {
                const index = slot.possibleValues.indexOf(key);
                correspondingValues = index !== -1 ? [slot.displayValues[index]] : [value];
            }

            return [key, correspondingValues.join(',')];
        })
    );
};

export const processFilledSlots = (
    filledSlots: Record<string, string>,
    slotsRequired: SlotsRequired[]
): Record<string, string> => {
    if (!slotsRequired || slotsRequired.length === 0) {
        console.error('slotsRequired array is empty or not provided');
        return filledSlots;
    }

    // Process filled slots to convert multi-selector values
    // from format "value1,value2" to format "['value1', 'value2']"
    const processedSlots = Object.entries(filledSlots).map(([key, value]) => {
        const slot = slotsRequired.find((slot) => slot.name === key);

        if (slot && slot.isMultiSelector && slot.multiSelectorType === 'checkbox') {
            const valuesArray = value.split(',').map((val) => `${val.trim()}`);
            return [key, `[${valuesArray.join(', ')}]`];
        } else {
            return [key, value];
        }
    });

    return Object.fromEntries(processedSlots);
};

export const openLinkInNewTab = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const getSourceDetails = (
    botDetail: IBotDetail,
    context: Context
): { document: string; website: string; api: string } => {
    return {
        document: getDocumentById(botDetail, context.sourceId)?.documentName,
        website: getWebsiteById(botDetail, context.sourceId)?.baseUrl,
        api: getApiById(botDetail, context.sourceId)?.intentDetail?.name
    };
};

export const getDocumentById = (botDetail: IBotDetail, sourceId: string): IDocument => {
    return botDetail.documents.find((x: IDocument) => x.id === sourceId);
};

export const getWebsiteById = (botDetail: IBotDetail, sourceId: string): ISourceWebsite => {
    return botDetail.websites.find((x: ISourceWebsite) => x.id === sourceId);
};

export const getApiById = (botDetail: IBotDetail, sourceId: string): Intent => {
    return botDetail.apis.find((x: Intent) => x.intentDetail?.id === sourceId);
};

export const slotValueLengthValidator = (intent: Intent, fieldName: string, slotValue: string) => {
    const valueLength = String(Number(slotValue.trim()))?.length;
    const slot = intent.slots.find((slot) => slot.slotName === fieldName);

    if (!slot) return true;

    if (!(slot.minCharacters ?? slot.maxCharacters)) return true;

    if (slot.minCharacters === slot.maxCharacters && valueLength !== slot.minCharacters) {
        return `Must be exactly ${slot.minCharacters} characters`;
    }
    if (valueLength < slot.minCharacters) {
        return `Must be at least ${slot.minCharacters} characters`;
    }
    if (valueLength > slot.maxCharacters) {
        return `Must be no more than ${slot.maxCharacters} characters`;
    }

    return true;
};
